import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Construction, Search, UserCog, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ProgressBar } from "@/components/technique/TechSummary";
import { NSelect } from "@/components/technique/fields";
import AssignTechDialog from "@/components/technique/AssignTechDialog";

const TECH_STATUSES = ["Non démarré", "Préparation", "En cours", "En pause", "Pré-réception", "Réceptionné", "Clôturé"];

function ProjectRow({ r, sup, onOpen, onAssign }) {
  const { project: p, file: f, summary: s } = r;
  return (
    <TableRow data-testid={`suivi-row-${p.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(r)}>
      <TableCell className="font-semibold">{p.nom}<span className="block font-mono text-[10px] text-slate-400">{p.numero}{f ? ` · ${f.numero}` : ""}</span></TableCell>
      <TableCell className="text-slate-600">{p.client_name || "—"}</TableCell>
      <TableCell className="text-slate-600">{f?.chantier_adresse || f?.chantier_nom || "—"}</TableCell>
      <TableCell className="text-slate-600" data-testid={`suivi-tech-${p.id}`}>{f?.technicien_name || <span className="text-amber-600 text-xs">Non affecté</span>}</TableCell>
      <TableCell className="text-xs text-slate-500">{f?.date_fin_prevue ? fmtDate(f.date_fin_prevue) : "—"}</TableCell>
      <TableCell><div className="flex items-center gap-2 min-w-[120px]"><ProgressBar value={s?.avancement || 0} /><span className="font-mono text-xs w-11 text-right">{s?.avancement || 0} %</span></div></TableCell>
      <TableCell><div className="flex items-center gap-1.5"><StatusBadge value={f?.statut || "Non démarré"} />{r.en_retard && <AlertTriangle className="h-4 w-4 text-rose-600" data-testid={`suivi-late-${p.id}`} />}</div></TableCell>
      {sup && <TableCell onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="outline" onClick={() => onAssign(r)} data-testid={`suivi-assign-${p.id}`}><UserCog className="h-4 w-4 mr-1" />{f?.technicien_id ? "Modifier" : "Affecter"}</Button>
      </TableCell>}
    </TableRow>
  );
}

export default function TechSuivi() {
  const nav = useNavigate();
  const [scope, setScope] = useState("all");
  const [closed, setClosed] = useState(false);
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [fStat, setFStat] = useState("");
  const [fTech, setFTech] = useState("");
  const [late, setLate] = useState(false);
  const [assign, setAssign] = useState(null);
  const load = () => api.get("/technique/projects", { params: { scope, include_closed: closed } }).then((r) => setD(r.data));
  useEffect(() => { setD(null); load(); }, [scope, closed]); // eslint-disable-line
  const open = async (r) => {
    try { const res = await api.post(`/technique/projects/${r.project.id}/open`); nav(`/technique/dossier/${res.data.id}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!d) return <Loader />;
  const sup = d.supervisor;
  const techs = [...new Map(d.rows.filter((r) => r.file?.technicien_id).map((r) => [r.file.technicien_id, r.file.technicien_name])).entries()];
  const rows = d.rows.filter((r) => (!q || `${r.project.nom} ${r.project.client_name} ${r.file?.chantier_nom || ""}`.toLowerCase().includes(q.toLowerCase()))
    && (!fStat || (r.file?.statut || "Non démarré") === fStat) && (!fTech || (fTech === "_none" ? !r.file?.technicien_id : r.file?.technicien_id === fTech)) && (!late || r.en_retard));
  return (
    <div>
      <PageHeader title="Suivi technique" subtitle={sup ? "Tous les projets clients — affectation des techniciens et supervision de l'exécution" : "Les projets qui vous sont affectés"} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {sup ? <Tabs value={scope} onValueChange={setScope}><TabsList>
          <TabsTrigger value="all" data-testid="suivi-scope-all">Tous les projets</TabsTrigger>
          <TabsTrigger value="mine" data-testid="suivi-scope-mine">Mes projets</TabsTrigger>
        </TabsList></Tabs> : <span className="rounded-full bg-brand-50 text-brand-700 text-sm font-semibold px-3 py-1" data-testid="suivi-scope-label">Mes projets</span>}
        <div className="relative w-full sm:w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" data-testid="suivi-search" /></div>
        <div className="w-44"><NSelect value={fStat} onChange={setFStat} placeholder="Tous statuts" options={TECH_STATUSES} testid="suivi-filter-statut" /></div>
        {sup && <div className="w-44"><NSelect value={fTech} onChange={setFTech} placeholder="Tous techniciens" options={[{ value: "_none", label: "Non affecté" }, ...techs.map(([v, l]) => ({ value: v, label: l }))]} testid="suivi-filter-tech" /></div>}
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={late} onChange={(e) => setLate(e.target.checked)} data-testid="suivi-filter-late" />En retard</label>
        {sup && scope === "all" && <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} data-testid="suivi-filter-closed" />Inclure terminés / annulés</label>}
      </div>
      {rows.length === 0 ? <EmptyState icon={Construction} title="Aucun projet" subtitle={sup ? "Aucun projet client ne correspond aux filtres." : "Aucun projet ne vous est encore affecté."} /> : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Projet</TableHead><TableHead>Client</TableHead><TableHead>Localisation</TableHead><TableHead>Technicien</TableHead>
              <TableHead>Fin prévue</TableHead><TableHead>Avancement</TableHead><TableHead>Statut technique</TableHead>{sup && <TableHead />}
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => <ProjectRow key={r.project.id} r={r} sup={sup} onOpen={open} onAssign={setAssign} />)}</TableBody>
          </Table>
        </div></div>
      )}
      {assign && <AssignTechDialog row={assign} onClose={() => setAssign(null)} onDone={() => { setAssign(null); load(); }} />}
    </div>
  );
}
